"use client";

import { useEffect } from "react";

/**
 * FREEZE THE PAGE BEHIND AN OVERLAY — COUNTED, SO THE LAST ONE OUT RESTORES IT
 *
 * Four surfaces froze the page with their own copy of
 * `body.style.overflow = "hidden"` and a cleanup that set it back to `""`:
 * `ImageLightbox`, `PostComposer`, `PostEditor` and the notification-settings
 * sheet. A bare set/reset is right for exactly one open surface and wrong the
 * moment two overlap, because the cleanup does not ask whether anybody else
 * still wants the page still.
 *
 * That is not hypothetical for the lightbox: its sidebar renders
 * `CommentThread` → `CommentItem`, and a comment's own image opens a SECOND
 * `ImageLightbox`. Closing the inner one released the lock while the outer was
 * still up, and the page scrolled behind a backdrop still covering it
 * (OSO-473).
 *
 * So the lock is counted, and the FIRST acquirer records whatever inline value
 * was there to begin with so the LAST release restores precisely that, rather
 * than assuming the page started at `""`.
 *
 * NOT `useEscapeLayer`, WHICH IS THE OTHER REGISTRY IN THIS DIRECTORY. That
 * one answers "which single open surface is topmost", for a key exactly one
 * surface may answer, and only the three surfaces that arbitrate Escape are in
 * it. Three of the four callers here are deliberately not layers, and
 * enrolling them to borrow a count would change which surface answers Escape.
 * "How many surfaces want the page frozen" is a different question over a
 * different membership, and nothing about it is topmost-shaped.
 */

let depth = 0;
/** The page's own inline `overflow`, as it was before the first lock. */
let restore = "";

export function useBodyScrollLock(locked: boolean = true) {
  useEffect(() => {
    if (!locked) return;
    if (depth === 0) {
      restore = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    depth += 1;
    return () => {
      depth -= 1;
      if (depth === 0) document.body.style.overflow = restore;
    };
  }, [locked]);
}
